import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  X, UploadCloud, FileSpreadsheet, CheckCircle2, AlertTriangle, Copy,
  Download, RefreshCw, Loader2, PackageCheck, Globe2, Truck, Ban,
  Plane, RotateCcw, ExternalLink,
} from "lucide-react";

const PHASES = [
  { key: "parsing", label: "Reading workbook" },
  { key: "validating", label: "Validating rows" },
  { key: "duplicates", label: "Checking duplicates (FY)" },
  { key: "ready", label: "Review" },
  { key: "importing", label: "Importing" },
  { key: "done", label: "Done" },
];

// Iter 272 — what a re-upload actually changed on overwritten parcels.
const ChangeSummary = ({ r }) => {
  const list = r.changes || [];
  return (
    <div className="mt-4 rounded-lg border border-sky-200 bg-white text-left" data-testid="wizard-change-summary">
      <div className="flex flex-wrap gap-3 border-b border-sky-100 bg-sky-50 px-3 py-2 text-xs text-sky-900">
        <b>Upload change summary</b>
        <span>Changed parcels: <b data-testid="wizard-changed-count">{list.length}</b></span>
        <span>Dates moved: <b data-testid="wizard-dates-moved">{r.dates_moved || 0}</b></span>
        <span>Replaced with identical data: <b>{r.unchanged_overwrites || 0}</b></span>
      </div>
      {list.length === 0 ? (
        <p className="px-3 py-3 text-xs text-slate-500">Every overwritten parcel was identical — nothing changed.</p>
      ) : (
        <div className="max-h-72 overflow-auto divide-y text-xs">
          {list.map((c, i) => (
            <div key={i} className="px-3 py-2" data-testid={`wizard-change-${i}`}>
              <div className="font-semibold text-slate-800">{c.customer} <span className="font-mono text-slate-500">{c.tracking || "no tracking"}</span></div>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {c.fields.map((f, k) => (
                  <span key={k} className={`rounded px-1.5 py-0.5 ${f.field === "Date" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}`}>
                    {f.field}: <s className="opacity-60">{f.old || "—"}</s> → <b>{f.new || "—"}</b>
                  </span>
                ))}
                {c.qty.map((q, k) => (
                  <span key={`q${k}`} className="rounded bg-indigo-50 px-1.5 py-0.5 text-indigo-700">
                    {q.medicine}: <s className="opacity-60">{q.old}</s> → <b>{q.new}</b>
                  </span>
                ))}
                {c.added.map((a, k) => <span key={`a${k}`} className="rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700">+ {a}</span>)}
                {c.removed.map((a, k) => <span key={`r${k}`} className="rounded bg-red-50 px-1.5 py-0.5 text-red-700">− {a}</span>)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


const TONES = {
  slate: "border-slate-200 text-slate-700", emerald: "border-emerald-200 text-emerald-700",
  amber: "border-amber-200 text-amber-700", red: "border-red-200 text-red-700",
  sky: "border-sky-200 text-sky-700", violet: "border-violet-200 text-violet-700",
};
const Stat = ({ label, value, tone = "slate", testid }) => (
  <div className={`rounded-xl border bg-white px-4 py-3 ${TONES[tone]}`} data-testid={testid}>
    <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-2xl font-semibold tabular-nums">{value}</div>
  </div>
);

const Chips = ({ icon: Icon, title, items, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600 mb-2">
      <Icon className="w-3.5 h-3.5" /> {title}
    </div>
    <div className="flex flex-wrap gap-1.5">
      {(items || []).map(i => (
        <span key={i.name} className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
          {i.name} <b className="tabular-nums">{i.count}</b>
        </span>
      ))}
    </div>
  </div>
);

const ProgressSlate = ({ job }) => {
  const idx = PHASES.findIndex(p => p.key === job?.phase);
  return (
    <div className="max-w-2xl mx-auto mt-16" data-testid="wizard-progress">
      <div className="flex items-end justify-between mb-2">
        <div className="text-sm text-slate-600 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
          <span data-testid="wizard-progress-message">{job?.message || "Starting…"}</span>
        </div>
        <div className="text-4xl font-semibold text-indigo-700 tabular-nums" data-testid="wizard-progress-pct">
          {job?.progress ?? 0}%
        </div>
      </div>
      <div className="h-3 rounded-full bg-slate-200 overflow-hidden">
        <div className="h-full bg-indigo-600 transition-[width] duration-500"
             style={{ width: `${job?.progress ?? 0}%` }} />
      </div>
      <ol className="mt-6 grid grid-cols-3 sm:grid-cols-6 gap-2 text-[11px]">
        {PHASES.map((p, i) => (
          <li key={p.key} className={`rounded-md px-2 py-1.5 text-center border ${
            i < idx ? "bg-emerald-50 border-emerald-200 text-emerald-700"
              : i === idx ? "bg-indigo-50 border-indigo-300 text-indigo-700 font-medium"
              : "bg-white border-slate-200 text-slate-400"}`}>{p.label}</li>
        ))}
      </ol>
    </div>
  );
};

const ACTION_STYLES = {
  skip: "bg-slate-700 text-white",
  allow: "bg-emerald-600 text-white",
  overwrite: "bg-amber-600 text-white",
};

const ParcelRow = ({ p, choice, onChoice, onFixName }) => {
  const actions = p.status === "duplicate"
    ? (p.existing.length ? ["skip", "allow", "overwrite"] : ["skip", "allow"])
    : p.status === "error" ? [] : ["allow", "skip"];
  return (
    <div className="border border-slate-200 rounded-lg bg-white p-3" data-testid={`wizard-parcel-${p.pid}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <b className="text-slate-800">{p.customer_name || "—"}</b>
        <span className="text-xs text-slate-500">{p.code || "no code"} · {p.date} · {p.mode} · {p.country}</span>
        <span className="text-xs font-mono px-1.5 rounded bg-slate-100">{p.tracking || "no tracking"}</span>
        {p.status === "error" && p.reasons.map(r => (
          <span key={r} className="text-xs px-1.5 rounded bg-red-50 text-red-700 border border-red-200">{r}</span>
        ))}
        {p.status === "valid" && p.why && (
          <span className="text-xs px-1.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200"
                data-testid={`wizard-why-${p.pid}`}>Why ready: {p.why}</span>
        )}
        {p.status === "valid" && p.reg_name && onFixName && (
          <button type="button" onClick={() => onFixName(p.pid)}
                  className="text-xs px-2 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors"
                  title={`Replace "${p.customer_name}" with the register's "${p.reg_name}" and mark as duplicate`}
                  data-testid={`wizard-fix-name-${p.pid}`}>
            Use register name “{p.reg_name}”
          </button>
        )}
        {p.dup_rule && p.status !== "error" && (
          <span className="text-xs px-1.5 rounded bg-amber-50 text-amber-700 border border-amber-200">{p.dup_rule}</span>
        )}
        {p.status === "possible_dup" && (
          <span className={`text-xs px-1.5 rounded font-bold border ${p.high_risk ? "bg-red-600 text-white border-red-700 animate-pulse" : "bg-orange-50 text-orange-700 border-orange-300"}`}
                data-testid={`wizard-possible-dup-${p.pid}`}>
            {(p.dup_rule || "").startsWith("Inside this file")
              ? (p.high_risk ? "HIGH RISK — same tracking twice in this file" : "Possible duplicate — same order twice in this file")
              : (p.high_risk ? "HIGH RISK — same tracking already in register" : "Possible duplicate — same order, no tracking")}
          </span>
        )}
        {p.status === "reship" && (
          <span className="text-xs px-1.5 rounded bg-violet-50 text-violet-700 border border-violet-200">Reship (T&amp;C)</span>
        )}
        {actions.length > 0 && (
          <div className="ml-auto inline-flex rounded-md border border-slate-300 overflow-hidden text-xs">
            {actions.map(c => (
              <button key={c} onClick={() => onChoice(p.pid, c)}
                      data-testid={`wizard-dup-${c}-${p.pid}`}
                      className={`px-2.5 py-1 capitalize transition-colors ${choice === c
                        ? ACTION_STYLES[c] : "bg-white text-slate-600 hover:bg-slate-50"}`}>{p.status === "possible_dup" ? (c === "allow" ? "Accept" : "Ignore") : c}</button>
            ))}
          </div>
        )}
      </div>
      <div className={`grid gap-2 mt-2 text-xs ${p.existing?.length ? "md:grid-cols-2" : ""}`}>
        <div className="rounded bg-slate-50 p-2">
          <div className="text-[10px] uppercase text-slate-400 mb-1">{p.existing?.length ? "New (this file)" : "Products"}</div>
          {p.rows.map((r, i) => <div key={i}>{r.medicine || <i className="text-red-500">missing</i>} × {r.quantity || <i className="text-red-500">missing</i>}</div>)}
        </div>
        {p.existing?.length > 0 && (
          <div className="rounded bg-amber-50/60 p-2">
            <div className="text-[10px] uppercase text-amber-600 mb-1">{p.status === "possible_dup" ? ((p.dup_rule || "").startsWith("Inside this file") ? "Earlier parcel in this same file" : "Matched order(s) in last 70 days") : "Existing in register"}</div>
            {p.existing.map(e => (
              <div key={e.id}>{e.date} · {e.medicine} × {e.quantity}{e.invoice_no ? ` · Inv ${e.invoice_no}` : ""}
                {p.status === "possible_dup" && e.tracking && <span className="ml-1 font-mono text-slate-500">{e.tracking}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const OrderUploadWizard = ({ open, onClose, onImported }) => {
  const [job, setJob] = useState(null);
  const [tab, setTab] = useState("duplicate");
  const [lists, setLists] = useState({ valid: [], duplicate: [], possible_dup: [], error: [], emirates: [], reship: [] });
  const [dupAction, setDupAction] = useState("skip");
  const [overrides, setOverrides] = useState({});
  const [drag, setDrag] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const stopPoll = () => { if (pollRef.current) clearInterval(pollRef.current); pollRef.current = null; };
  const reset = () => { stopPoll(); setJob(null); setLists({ valid: [], duplicate: [], possible_dup: [], error: [], emirates: [], reship: [] }); setOverrides({}); setDupAction("skip"); };
  useEffect(() => () => stopPoll(), []);
  useEffect(() => { if (!open) reset(); }, [open]);

  const loadLists = useCallback(async (id, keepTab = false) => {
    try {
      const h = { headers: getAuthHeader() };
      const keys = ["valid", "duplicate", "possible_dup", "error", "emirates", "reship"];
      const res = await Promise.all(keys.map(s =>
        axios.get(`${API}/order-register/wizard/${id}/parcels`, { ...h, params: { status: s, limit: 2000 } })));
      const out = Object.fromEntries(keys.map((k, i) => [k, res[i].data.items]));
      setLists(out);
      if (!keepTab) setTab(["possible_dup", "duplicate", "valid", "emirates", "reship", "error"].find(s => out[s].length) || "duplicate");
    } catch (e) {
      // Iter 287 — surface it instead of silently showing empty tabs
      toast.error(e.response?.data?.detail || "Could not load the review lists — please retry");
    }
  }, []);

  // Iter 279 — Name Mismatch Fixer (one parcel or all)
  const fixNames = async (pid) => {
    try {
      const r = await axios.post(`${API}/order-register/wizard/${job.job_id}/fix-names`,
        pid ? { pid } : {}, { headers: getAuthHeader() });
      setJob(j => ({ ...j, stats: r.data.stats }));
      await loadLists(job.job_id, true);
      toast.success(`${r.data.count} name${r.data.count === 1 ? "" : "s"} fixed — moved to Duplicates`);
    } catch (e) { toast.error(e.response?.data?.detail || "Fix failed"); }
  };
  const mismatchCount = lists.valid.filter(p => p.reg_name).length;

  const poll = useCallback((id) => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/order-register/wizard/${id}`, { headers: getAuthHeader() });
        setJob(r.data);
        if (["ready", "done", "failed"].includes(r.data.phase)) {
          stopPoll();
          if (r.data.phase === "ready") loadLists(id);
          if (r.data.phase === "done") { toast.success(`Imported ${r.data.result.inserted_rows} rows`); onImported?.(); }
          if (r.data.phase === "failed") toast.error(r.data.error);
        }
      } catch (e) { stopPoll(); toast.error(e.response?.data?.detail || "Lost upload session"); }
    }, 1000);
  }, [loadLists, onImported]);

  const startFile = async (file) => {
    if (!file) return;
    reset();
    setJob({ phase: "parsing", progress: 0, message: "Uploading file", filename: file.name });
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await axios.post(`${API}/order-register/wizard/analyze`, fd,
        { headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setJob(r.data); poll(r.data.job_id);
    } catch (e) { setJob(null); toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const commit = async () => {
    try {
      const r = await axios.post(`${API}/order-register/wizard/${job.job_id}/commit`,
        { duplicate_action: dupAction, overrides }, { headers: getAuthHeader() });
      setJob(r.data); poll(r.data.job_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
  };

  const openPreview = async (st) => {
    const win = window.open("", "_blank");
    if (win) win.document.write("<p style='font-family:sans-serif;padding:16px'>Loading…</p>");
    try {
      const r = await axios.get(`${API}/order-register/wizard/${job.job_id}/preview`,
        { headers: getAuthHeader(), params: { status: st }, responseType: "text" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/html" }));
      if (win) win.location.href = url; else window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch { if (win) win.close(); toast.error("Preview failed"); }
  };

  const downloadCompare = async () => {
    try {
      const r = await axios.get(`${API}/order-register/wizard/${job.job_id}/compare.xlsx`,
        { headers: getAuthHeader(), responseType: "blob" });
      const m = (r.headers["content-disposition"] || "").match(/filename="?([^";]+)"?/);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = m ? m[1] : "compare.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const downloadErrors = async () => {
    try {
      const r = await axios.get(`${API}/order-register/wizard/${job.job_id}/error-sheet`,
        { headers: getAuthHeader(), responseType: "blob" });
      const m = (r.headers["content-disposition"] || "").match(/filename="?([^";]+)"?/);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = m ? m[1] : "errors.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const setAll = (a) => {
    setDupAction(a);
    const dupIds = new Set(lists.duplicate.map(p => p.pid));
    setOverrides(o => Object.fromEntries(Object.entries(o).filter(([pid]) => !dupIds.has(pid))));
  };
  // HIGH RISK possible duplicates (already shipped) default to Ignore.
  const choiceOf = (p) => overrides[p.pid] || (p.status === "duplicate" ? dupAction : (p.status === "possible_dup" && p.high_risk && !(p.dup_rule || "").startsWith("Inside this file") ? "skip" : "allow"));
  if (!open) return null;

  const s = job?.stats;
  const busy = job && !["ready", "done", "failed"].includes(job.phase);
  const chosen = (st) => lists[st].filter(p => choiceOf(p));
  const dupAllow = chosen("duplicate").filter(p => choiceOf(p) === "allow" || (choiceOf(p) === "overwrite" && !p.existing.length)).length;
  const dupOverwrite = chosen("duplicate").filter(p => choiceOf(p) === "overwrite" && p.existing.length).length;
  const emiAllow = chosen("emirates").filter(p => choiceOf(p) === "allow").length;
  const reshipAllow = chosen("reship").filter(p => choiceOf(p) === "allow").length;
  const pdAllow = chosen("possible_dup").filter(p => choiceOf(p) === "allow").length;
  const readyAllow = chosen("valid").filter(p => choiceOf(p) === "allow").length;
  const importParcels = readyAllow + dupAllow + dupOverwrite + emiAllow + reshipAllow + pdAllow;
  const skipCount = (s?.total_parcels || 0) - importParcels - (s?.parcels.error || 0);

  return (
    <div className="fixed inset-0 z-[60] bg-slate-50 flex flex-col animate-in fade-in duration-200" data-testid="order-upload-wizard">
      <header className="flex items-center gap-3 px-6 py-4 border-b bg-white">
        <FileSpreadsheet className="w-5 h-5 text-indigo-600" />
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Order Register Upload</h2>
          <p className="text-xs text-slate-500">
            {job?.filename ? <>File: <b data-testid="wizard-filename">{job.filename}</b> · </> : null}
            Duplicate scope: <b data-testid="wizard-fy">{job?.fy?.label || "current financial year"}</b>
          </p>
        </div>
        {job && ["ready", "done"].includes(job.phase) && (
          <Button variant="outline" size="sm" className="ml-auto border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  onClick={downloadCompare}
                  title="Side-by-side Excel of this sheet vs the register — differences highlighted amber"
                  data-testid="wizard-download-compare">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Compare sheet
          </Button>
        )}
        <Button variant="ghost" size="sm" className={job && ["ready","done"].includes(job.phase) ? "" : "ml-auto"} onClick={onClose} disabled={job?.phase === "importing"}
                data-testid="wizard-close"><X className="w-4 h-4" /></Button>
      </header>

      <main className="flex-1 overflow-auto px-6 py-6">
        {!job && (
          <div onDragOver={e => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
               onDrop={e => { e.preventDefault(); setDrag(false); startFile(e.dataTransfer.files?.[0]); }}
               onClick={() => fileRef.current?.click()} data-testid="wizard-dropzone"
               className={`max-w-2xl mx-auto mt-16 cursor-pointer rounded-2xl border-2 border-dashed p-16 text-center transition-colors ${
                 drag ? "border-indigo-500 bg-indigo-50" : "border-slate-300 bg-white hover:border-indigo-400"}`}>
            <UploadCloud className="w-12 h-12 mx-auto text-indigo-500" />
            <div className="mt-3 text-base font-medium text-slate-700">Drop your Order Register Excel here</div>
            <div className="text-sm text-slate-500">or click to browse (.xlsx)</div>
            <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden" data-testid="wizard-file-input"
                   onChange={e => startFile(e.target.files?.[0])} />
          </div>
        )}

        {busy && <ProgressSlate job={job} />}

        {job?.phase === "failed" && (
          <div className="max-w-xl mx-auto mt-16 text-center" data-testid="wizard-failed">
            <Ban className="w-10 h-10 mx-auto text-red-500" />
            <div className="mt-2 text-red-700">{job.error}</div>
            <Button className="mt-4" onClick={reset} data-testid="wizard-retry">Try another file</Button>
          </div>
        )}

        {s && ["ready", "done"].includes(job.phase) && (
          <div className="max-w-6xl mx-auto space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Total rows" value={s.total_rows} testid="wizard-stat-rows" />
              <Stat label="Orders / parcels" value={s.total_parcels} testid="wizard-stat-parcels" />
              <Stat label="Customers" value={s.total_customers} testid="wizard-stat-customers" />
              <Stat label="Ready" value={s.parcels.valid} tone="emerald" testid="wizard-stat-valid" />
              <Stat label="Duplicates" value={s.parcels.duplicate} tone="amber" testid="wizard-stat-dups" />
              <Stat label="Emirates / No-tracking" value={s.parcels.emirates} tone="sky" testid="wizard-stat-emirates" />
              <Stat label="Reship" value={s.parcels.reship} tone="violet" testid="wizard-stat-reship" />
              <Stat label="Possible duplicate" value={s.parcels.possible_dup || 0} tone="amber" testid="wizard-stat-possible-dup" />
              <Stat label="Errors" value={s.parcels.error} tone="red" testid="wizard-stat-errors" />
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              <Chips icon={Truck} title="By service" items={s.by_service} testid="wizard-by-service" />
              <Chips icon={Globe2} title="By country" items={s.by_country} testid="wizard-by-country" />
              <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm" data-testid="wizard-empty-tracking">
                <div className="text-xs font-medium text-slate-600 mb-1">Empty tracking</div>
                <b className="text-xl tabular-nums">{s.empty_tracking}</b> parcel(s) without tracking
                <div className="text-xs text-slate-500">Imported; matched parcels land on the Emirates / No-tracking tab.</div>
              </div>
            </div>

            {job.phase === "done" && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="wizard-done">
                <div className="flex items-center gap-2 text-emerald-800 font-medium">
                  <CheckCircle2 className="w-5 h-5" /> Import complete {job.result.verified ? "· verified" : "· check counts"}
                </div>
                <div className="mt-2 flex flex-wrap gap-2 text-sm text-slate-700">
                  <span>Rows inserted: <b data-testid="wizard-result-inserted">{job.result.inserted_rows}</b></span>·
                  <span>Parcels: <b>{job.result.inserted_parcels}</b></span>·
                  <span>Overwritten: <b data-testid="wizard-result-overwritten">{job.result.overwritten_parcels}</b></span>·
                  <span>Allowed matches: <b data-testid="wizard-result-allowed">{job.result.allowed_parcels}</b></span>·
                  <span>Skipped: <b data-testid="wizard-result-skipped">{job.result.skipped_parcels}</b></span>·
                  <span>Errors not imported: <b>{job.result.error_parcels}</b></span>
                </div>
                {job.result.overwritten_parcels > 0 && <ChangeSummary r={job.result} />}
              </div>
            )}

            {job.phase === "ready" && (
              <div className="rounded-xl border border-slate-200 bg-white">
                <div className="flex flex-wrap items-center gap-2 border-b px-3 py-2">
                  {[["valid", `Ready to upload (${s.parcels.valid})`, PackageCheck],
                    ["duplicate", `Duplicates (${s.parcels.duplicate})`, Copy],
                    ["possible_dup", `Possible duplicate (${s.parcels.possible_dup || 0})`, AlertTriangle],
                    ["emirates", `Emirates / No-tracking (${s.parcels.emirates})`, Plane],
                    ["reship", `Reship (${s.parcels.reship})`, RotateCcw],
                    ["error", `Errors (${s.parcels.error})`, AlertTriangle]].map(([k, l, I]) => (
                    <button key={k} onClick={() => setTab(k)} data-testid={`wizard-tab-${k}`}
                            className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm transition-colors ${tab === k ? "bg-indigo-50 text-indigo-700 font-medium" : "text-slate-600 hover:bg-slate-50"}`}>
                      <I className="w-4 h-4" /> {l}
                    </button>
                  ))}
                  {tab === "duplicate" && s.parcels.duplicate > 0 && (
                    <div className="ml-auto flex items-center gap-2 text-xs">
                      <span className="text-slate-500">Apply to all:</span>
                      <Button size="sm" variant={dupAction === "skip" ? "default" : "outline"} onClick={() => setAll("skip")} data-testid="wizard-dup-all-skip">Skip all</Button>
                      <Button size="sm" variant={dupAction === "allow" ? "default" : "outline"} onClick={() => setAll("allow")}
                              className={dupAction === "allow" ? "bg-emerald-600 hover:bg-emerald-700" : ""} data-testid="wizard-dup-all-allow">Allow all</Button>
                      <Button size="sm" variant={dupAction === "overwrite" ? "default" : "outline"} onClick={() => setAll("overwrite")}
                              className={dupAction === "overwrite" ? "bg-amber-600 hover:bg-amber-700" : ""} data-testid="wizard-dup-all-overwrite">Overwrite all</Button>
                    </div>
                  )}
                  <Button size="sm" variant="outline"
                          className={`border-sky-300 text-sky-700 hover:bg-sky-50 ${tab === "duplicate" && s.parcels.duplicate > 0 ? "" : "ml-auto"}`}
                          disabled={!lists[tab].length} onClick={() => openPreview(tab)}
                          data-testid="wizard-preview-tab">
                    <ExternalLink className="w-4 h-4 mr-1" /> Open preview in new tab
                  </Button>
                  {tab === "error" && s.parcels.error > 0 && (
                    <Button size="sm" variant="outline" onClick={downloadErrors} data-testid="wizard-download-errors">
                      <Download className="w-4 h-4 mr-1" /> Download error sheet
                    </Button>
                  )}
                </div>
                {tab === "valid" && s.parcels.valid > 0 && (
                  <div className="px-3 py-2 text-xs text-emerald-800 bg-emerald-50 border-b" data-testid="wizard-ready-hint">
                    These parcels have no match in the register for this financial year — each one shows <b>why</b> it is not a duplicate. Imported by default; choose Skip per parcel to keep one out.
                    {mismatchCount > 0 && (
                      <Button size="sm" variant="outline" className="ml-2 h-6 px-2 text-[11px] border-amber-300 bg-white text-amber-800 hover:bg-amber-50"
                              onClick={() => fixNames(null)} data-testid="wizard-fix-all-names">
                        Fix all {mismatchCount} name mismatch{mismatchCount === 1 ? "" : "es"}
                      </Button>
                    )}
                  </div>
                )}
                {tab === "possible_dup" && (s.parcels.possible_dup || 0) > 0 && (
                  <div className="px-3 py-2 text-xs text-orange-800 bg-orange-50 border-b flex flex-wrap items-center gap-2" data-testid="wizard-possible-dup-hint">
                    <span>Only 2 rules (70 days, and also inside this same file): <b>Red HIGH RISK</b> = the same tracking number again · <b>Orange</b> = no tracking + same customer with the same products and quantities. <b>Accept</b> imports it with a blinking flag in the register; <b>Ignore</b> skips it.</span>
                    <span className="ml-auto inline-flex gap-1">
                      <button type="button" onClick={() => setOverrides(o => ({ ...o, ...Object.fromEntries(lists.possible_dup.map(p => [p.pid, "allow"])) }))}
                        className="px-2 py-0.5 rounded bg-emerald-600 text-white font-semibold" data-testid="wizard-pd-accept-all">Accept all</button>
                      <button type="button" onClick={() => setOverrides(o => ({ ...o, ...Object.fromEntries(lists.possible_dup.map(p => [p.pid, "skip"])) }))}
                        className="px-2 py-0.5 rounded bg-slate-700 text-white font-semibold" data-testid="wizard-pd-ignore-all">Ignore all</button>
                    </span>
                  </div>
                )}
                {tab === "emirates" && s.parcels.emirates > 0 && (
                  <div className="px-3 py-2 text-xs text-sky-700 bg-sky-50 border-b" data-testid="wizard-emirates-hint">
                    EMIRATES parcels (tracking arrives later) and split parcels are allowed by default — they import as new orders. Choose Skip per parcel to keep them out.
                  </div>
                )}
                {tab === "reship" && s.parcels.reship > 0 && (
                  <div className="px-3 py-2 text-xs text-violet-700 bg-violet-50 border-b" data-testid="wizard-reship-hint">
                    Notes mention "reship" — these are re-sent parcels on T&amp;C basis, imported as new by default. Choose Skip per parcel to keep them out.
                  </div>
                )}
                <div className="p-3 space-y-2 max-h-[45vh] overflow-auto" data-testid={`wizard-list-${tab}`}>
                  {lists[tab].length === 0 && <div className="text-sm text-slate-400 py-6 text-center">Nothing here — all clear.</div>}
                  {lists[tab].map(p => (
                    <ParcelRow key={p.pid} p={p} choice={choiceOf(p)}
                               onFixName={job.phase === "ready" ? fixNames : null}
                               onChoice={(pid, c) => setOverrides(o => ({ ...o, [pid]: c }))} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {job && ["ready", "done"].includes(job.phase) && (
        <footer className="flex flex-wrap items-center gap-2 px-6 py-3 border-t bg-white">
          {job.phase === "ready" ? (
            <>
              <span className="text-sm text-slate-600" data-testid="wizard-import-summary">
                Will import <b>{importParcels}</b> parcel(s) · ready <b>{readyAllow}</b> · overwrite <b>{dupOverwrite}</b> · allowed matches <b>{dupAllow + emiAllow + reshipAllow}</b> · possible duplicates accepted <b>{pdAllow}</b> · skip <b>{skipCount}</b> · errors excluded <b>{s.parcels.error}</b>
              </span>
              <Button variant="outline" className="ml-auto" onClick={reset} data-testid="wizard-choose-another">
                <RefreshCw className="w-4 h-4 mr-1" /> Choose another file
              </Button>
              <Button onClick={commit} disabled={importParcels === 0} className="bg-indigo-600 hover:bg-indigo-700" data-testid="wizard-commit">
                <PackageCheck className="w-4 h-4 mr-1" /> Import {importParcels} parcel(s)
              </Button>
            </>
          ) : (
            <>
              {job.result.error_parcels > 0 && (
                <Button variant="outline" onClick={downloadErrors} data-testid="wizard-done-download-errors">
                  <Download className="w-4 h-4 mr-1" /> Download error sheet
                </Button>
              )}
              <Button variant="outline" className="ml-auto" onClick={reset} data-testid="wizard-upload-fixed">
                <UploadCloud className="w-4 h-4 mr-1" /> Upload another / fixed sheet
              </Button>
              <Button onClick={onClose} data-testid="wizard-finish">Close</Button>
            </>
          )}
        </footer>
      )}
    </div>
  );
};
